import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate, Link } from 'react-router-dom';
import { Package, Inbox, Loader2, LogOut, ArrowUpRight, Phone, MessageCircle, MapPin, User, Home, Building2, Shield, X, Calendar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { heroImages } from '../mock/data';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const cls = {
    new: 'bg-[#c8102e] text-white',
    contacted: 'bg-amber-500 text-white',
    in_transit: 'bg-black text-white',
    delivered: 'bg-emerald-500 text-white',
    cancelled: 'bg-neutral-300 text-neutral-700',
  }[status || 'new'] || 'bg-neutral-200 text-black';
  return <span className={`text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 font-semibold ${cls}`}>{status || 'new'}</span>;
};

const PackageDetail = ({ pkg, onClose }) => {
  const dim = [pkg.length_cm, pkg.width_cm, pkg.height_cm].filter(Boolean).join(' × ');
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-black text-white p-5 flex items-center justify-between">
          <div className="flex items-center gap-3"><Package className="w-5 h-5 text-[#c8102e]" /><h3 className="font-serif-display font-bold text-lg">Parcel Request</h3><StatusBadge status={pkg.status} /></div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">From</div><div className="font-serif-display text-lg font-bold text-black">{pkg.from_country}</div></div>
            <div><div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">To</div><div className="font-serif-display text-lg font-bold text-black">{pkg.to_country}</div></div>
            <div className="col-span-2 flex items-center gap-2 text-neutral-500 text-xs"><Calendar className="w-3 h-3" /> {new Date(pkg.created_at).toLocaleString()}</div>
          </div>
          <div className="border-t border-neutral-100 pt-4">
            <h4 className="font-serif-display text-lg font-bold text-black mb-3 flex items-center gap-2"><Package className="w-4 h-4 text-[#c8102e]" /> Parcel</h4>
            <div className="text-sm text-neutral-700 space-y-1">
              <div>• Weight: <span className="font-semibold text-black">{pkg.weight_kg} kg</span></div>
              {dim && <div>• Dimensions: <span className="font-semibold text-black">{dim} cm</span></div>}
              {pkg.item_value_eur ? <div>• Declared value: <span className="font-semibold text-black">€{pkg.item_value_eur}</span></div> : null}
              {pkg.contents && <div>• Contents: {pkg.contents}</div>}
              {pkg.purpose && <div>• Purpose: {pkg.purpose}</div>}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {['sender', 'receiver'].map((k) => (
              <div key={k} className="border-t border-neutral-100 pt-4">
                <h4 className="font-serif-display text-lg font-bold text-black mb-3 flex items-center gap-2"><User className="w-4 h-4 text-[#c8102e]" /> {k === 'sender' ? 'Sender' : 'Receiver'}</h4>
                <div className="text-sm text-neutral-700 space-y-1">
                  <div className="font-semibold text-black">{pkg[`${k}_name`]}</div>
                  {pkg[`${k}_id`] && <div>ID: {pkg[`${k}_id`]}</div>}
                  <a href={`tel:${pkg[`${k}_phone`]}`} className="flex items-center gap-1.5 hover:text-[#c8102e]"><Phone className="w-3 h-3" /> {pkg[`${k}_phone`]}</a>
                  <div className="flex items-start gap-1.5"><MapPin className="w-3 h-3 mt-0.5 shrink-0" /><span>{pkg[`${k}_address`]}{pkg[`${k}_city`] ? ', ' + pkg[`${k}_city`] : ''}{pkg[`${k}_zip`] ? ' ' + pkg[`${k}_zip`] : ''}</span></div>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4 border-t border-neutral-100 pt-4">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">Delivery</div>
              <div className="flex items-center gap-2 font-semibold text-black text-sm">{pkg.delivery_method === 'branch' ? <><Building2 className="w-4 h-4 text-[#c8102e]" /> {pkg.branch_name || 'Branch'}</> : <><Home className="w-4 h-4 text-[#c8102e]" /> Home pickup</>}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">Insurance</div>
              <div className="flex items-center gap-2 font-semibold text-black text-sm"><Shield className="w-4 h-4 text-[#c8102e]" /> {pkg.insurance_plan}</div>
            </div>
          </div>
          {pkg.resolved_desk?.whatsapp && (
            <a href={`https://wa.me/${pkg.resolved_desk.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:brightness-110 text-white h-12 font-semibold"><MessageCircle className="w-4 h-4" /> Open desk WhatsApp — {pkg.resolved_desk.name}</a>
          )}
        </div>
      </div>
    </div>
  );
};

const Employee = () => {
  const { user, loading, logout, token } = useAuth();
  const { toast } = useToast();
  const [packages, setPackages] = useState(null);
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!token) return;
    const H = { headers: { Authorization: `Bearer ${token}` } };
    Promise.all([
      axios.get(`${API}/staff/packages`, H),
      axios.get(`${API}/staff/submissions`, H),
      axios.get(`${API}/staff/stats`, H),
    ]).then(([p, s, st]) => { setPackages(p.data); setSubs(s.data); setStats(st.data); })
      .catch(() => { setPackages([]); setStats({ packages_total: 0, packages_new: 0, submissions_total: 0, submissions_unread: 0 }); });
  }, [token]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#c8102e]" /></div>;
  if (!user) return <Navigate to="/login" state={{ from: '/employee' }} replace />;
  if (!user.is_admin && !user.is_employee) return <Navigate to="/account" replace />;

  return (
    <div>
      <section className="relative h-[280px] flex items-end bg-black overflow-hidden">
        <img src={heroImages.counter} alt="Employee" className="absolute inset-0 w-full h-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pb-8">
          <div className="flex items-center gap-3 mb-3"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow text-white">Staff Dashboard</span></div>
          <h1 className="text-white font-serif-display text-4xl md:text-5xl font-bold">Welcome, {user.name.split(' ')[0]}.</h1>
        </div>
      </section>

      <section className="py-12 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Total Parcels', value: stats?.packages_total ?? '—', accent: false },
              { label: 'New Parcels', value: stats?.packages_new ?? '—', accent: true },
              { label: 'Contact Messages', value: stats?.submissions_total ?? '—', accent: false },
              { label: 'Unread Messages', value: stats?.submissions_unread ?? '—', accent: true },
            ].map((s) => (
              <div key={s.label} className={`p-6 border ${s.accent ? 'bg-[#c8102e] text-white border-[#c8102e]' : 'bg-white text-black border-neutral-200'}`}>
                <div className="font-serif-display text-4xl font-bold">{s.value}</div>
                <div className={`text-[10px] tracking-[0.25em] uppercase mt-1 ${s.accent ? 'text-white/80' : 'text-neutral-500'}`}>{s.label}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Package className="w-5 h-5 text-[#c8102e]" />
                <h2 className="font-serif-display text-2xl font-bold text-black">Recent Parcel Requests</h2>
              </div>
              {packages === null ? (<Loader2 className="w-5 h-5 animate-spin text-[#c8102e]" />) : packages.length === 0 ? (
                <div className="bg-white p-8 border border-neutral-200 text-center text-sm text-neutral-500">No parcel requests yet.</div>
              ) : (
                <div className="space-y-2">
                  {packages.slice(0, 10).map((p) => (
                    <button key={p.id} onClick={() => setSelected(p)} className="w-full text-left bg-white border border-neutral-200 hover:border-black p-4 transition-colors">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-serif-display font-bold text-black text-sm">{p.sender_name} → {p.receiver_name}</span>
                        <StatusBadge status={p.status} />
                      </div>
                      <div className="text-xs text-neutral-500">{p.from_country} → {p.to_country} • {p.weight_kg}kg • {new Date(p.created_at).toLocaleDateString()}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Inbox className="w-5 h-5 text-[#c8102e]" />
                <h2 className="font-serif-display text-2xl font-bold text-black">Contact Inbox</h2>
              </div>
              {subs.length === 0 ? (
                <div className="bg-white p-8 border border-neutral-200 text-center text-sm text-neutral-500">No messages yet.</div>
              ) : (
                <div className="space-y-2">
                  {subs.slice(0, 10).map((s) => (
                    <div key={s.id} className="bg-white border border-neutral-200 p-4">
                      <div className="flex items-center justify-between mb-1"><span className="font-serif-display font-bold text-black text-sm">{s.name}</span><span className="text-xs text-neutral-400">{new Date(s.created_at).toLocaleDateString()}</span></div>
                      <div className="text-xs text-neutral-500 truncate">{s.subject || s.message.slice(0, 80)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="mt-10 flex items-center justify-between pt-6 border-t border-neutral-200">
            <Link to="/" className="text-sm text-neutral-500 hover:text-black flex items-center gap-2"><ArrowUpRight className="w-3.5 h-3.5" /> Back to website</Link>
            <button onClick={logout} className="flex items-center gap-2 text-sm text-neutral-500 hover:text-[#c8102e]"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
          </div>
        </div>
      </section>

      {selected && <PackageDetail pkg={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

export default Employee;
